import { AfterViewInit, Component, OnDestroy, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.heat';
import { AppService } from 'src/app/shared/services/app.service';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';

declare module 'leaflet' {
  function heatLayer(
    latlngs: Array<[number, number, number]>,
    options?: any
  ): L.Layer;
}

interface HeatmapLocation {
  longtitude: number;
  latitude: number;
}

interface Club {
  _id: string;
  name: string;
  loc?: {
    coordinates: [number, number];
    type: string;
  };
  status: 'accepted' | 'pending';
}

interface Disability {
  _id?: string;
  name: string;
  value?: string;
}

interface DisabilityColor {
  name: string;
  color: string;
}

interface BarSegment {
  disability: string;
  count: number;
  height: number;
  percentage: number;
  color: string;
}

@Component({
  selector: 'app-heatmap',
  templateUrl: './heatmap.component.html',
  styleUrls: ['./heatmap.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeatmapComponent implements OnInit, AfterViewInit, OnDestroy {
  private map: L.Map;
  private destroy$ = new Subject<void>();
  private heatLayer: L.Layer | null = null;

  // Cache για clubs data
  private clubsDataCache: { [key: string]: string } = {};
  private clubsLoading$ = new Subject<{ [key: string]: string }>();

  // Disability colors mapping
  disabilityColors: { [key: string]: string } = {
    MOBILITY: '#f23232ff',
    HEARING: '#f9a825',
    VISION: '#46d793ff',
    MENTAL: '#8e24aa',
    MENTALHEALTH: '#1e88e5',
    OTHER: '#9e9e9e'
  };

  // Filter form properties
  filterForm: UntypedFormGroup;
  problemTypeOptions = [
    { label: 'WITH', value: 1 },
    { label: 'WITHOUT', value: 0 }
  ];
  disabilitiesOptions: any[] = [];

  // Clubs data
  acceptedClubs: Club[] = [];
  pendingClubs: Club[] = [];
  acceptedClubsCount: number = 0;
  pendingClubsCount: number = 0;

  // Users pending verification data
  usersPendingByClub: { [clubId: string]: { clubName: string; count: number } } = {};
  usersNeedingUpdate: number = 0;
  currentCarouselSlide: number = 0;
  showFilters: boolean = false;

  // Statistics data
  activityProblemsStats: {
    no_activity_problems: number,
    activity_problems: number,
    disabilities: Array<{ count: number, disability: string }>
  } | null = null;
  ageGroupsStats: Array<{
    age_range: number[],
    count: number,
    disabilities: { [key: string]: number }
  }> = [];

  // Chart helpers
  maxAgeGroupCount: number = 0;
  disabilityLegendItems: DisabilityColor[] = [];
  pixelsPerUnit: number = 1;

  // UI state
  isLoading: boolean = true;
  isLoadingClubs: boolean = false;
  isLoadingMap: boolean = true;
  isLoadingUsers: boolean = false;
  isLoadingStats: boolean = false;
  errorMessage: string = '';


  activeDisability: string | null = null;
  activeRightChartDisability: string | null = null;

  constructor(
    private appService: AppService,
    private fb: UntypedFormBuilder,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.filterForm = this.fb.group({
      problemType: [''],
      disabilities: [[]]
    });
  }

  ngOnInit(): void {
    
    this.loadDisabilities();
    this.loadClubsDataOnce(); // NEW: Φορτώνει clubs ΜΟΝΟ ΜΙΑ ΦΟΡΑ
    this.initializeMapFromUserLocation();
    this.loadStatistics();
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  /**
   * NEW: Φορτώνει όλα τα clubs data (accepted + pending) σε ΜΙΑ κλήση
   * Χρησιμοποιεί forkJoin για παράλληλα requests
   */
  private loadClubsDataOnce(): void {
    this.isLoadingClubs = true;

    forkJoin({
      accepted: this.appService.fetch_sectors({
        status: 'accepted',
        clubs: true,
        operations: false
      }),
      pending: this.appService.fetch_sectors({
        status: 'pending',
        clubs: true,
        operations: false
      })
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // Αποθηκεύει τα accepted clubs
          if (response?.accepted?.clubs) {
            this.acceptedClubs = response.accepted.clubs;
            this.acceptedClubsCount = response.accepted.clubs.length;
          }

          // Αποθηκεύει τα pending clubs
          if (response?.pending?.clubs) {
            this.pendingClubs = response.pending.clubs;
            this.pendingClubsCount = response.pending.clubs.length;
          }

          // unified map όλων των clubs
          const clubMap: { [key: string]: string } = {};
          const allClubs = [
            ...(response?.accepted?.clubs || []),
            ...(response?.pending?.clubs || [])
          ];

          allClubs.forEach(club => {
            clubMap[club._id] = club.name;
          });

          // 
          this.clubsDataCache = clubMap;
          this.clubsLoading$.next(clubMap);

          // 
          this.loadUsersPendingByClub();

          this.isLoadingClubs = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.isLoadingClubs = false;
          console.error('Error loading clubs:', err);
          this.cdr.markForCheck();
        }
      });
  }

  private loadDisabilities(): void {
    this.appService.get_disabilities()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any[]) => {
          this.disabilitiesOptions = data;
          const allDisabilityIds = data.map(disability => disability._id);
          this.filterForm.patchValue({
            disabilities: allDisabilityIds
          });
        },
        error: (err) => {
          console.error('ERROR loading disabilities', err);
        }
      });
  }

  
  private loadUsersPendingByClub(): void {
    this.isLoadingUsers = true;

    // Αν τα clubs έχουν ήδη φορτωθεί, χρησιμοποιούμε το cache αμέσως
    if (Object.keys(this.clubsDataCache).length > 0) {
      this.performUserAggregation(this.clubsDataCache);
    } else {
      // Αν όχι (edge case), περιμένουμε το clubsLoading$ signal
      this.clubsLoading$
        .pipe(
          takeUntil(this.destroy$),
          distinctUntilChanged()
        )
        .subscribe(clubMap => {
          this.performUserAggregation(clubMap);
        });
    }
  }

  
  private performUserAggregation(clubMap: { [key: string]: string }): void {
    const clubIds = Object.keys(clubMap);

    const filters = {
      sort: -1,
      page: 1,
      limit: 1000,
      activity_problem: [0, 1],
      disabilities: this.disabilitiesOptions.map(d => d._id),
      lowest_age: 0,
      highest_age: 140,
      statuses: ['pending'],
      clubs: clubIds
    };

    this.appService.fetch_manager_users_with_filters(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (userResponse: any) => {
          this.isLoadingUsers = false;
          if (userResponse?.data && userResponse.data.length > 0) {
            this.aggregateUsersByClub(userResponse.data, clubMap);
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.isLoadingUsers = false;
          console.error('Error loading users pending verification:', err);
          this.cdr.markForCheck();
        }
      });
  }

  private aggregateUsersByClub(users: any[], clubMap: { [key: string]: string }): void {
    this.usersPendingByClub = {};

    users.forEach(user => {
      if (user.club && user.club.length > 0) {
        const clubId = user.club[0];
        if (!this.usersPendingByClub[clubId]) {
          this.usersPendingByClub[clubId] = {
            clubName: clubMap[clubId] || 'Unknown Club',
            count: 0
          };
        }
        this.usersPendingByClub[clubId].count++;
      }
    });
  }

  private initializeMapFromUserLocation(): void {
    this.appService.fetch_user()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response && response[0]?.municipalities?.[0]?.center?.coordinates) {
            const coordinates = response[0].municipalities[0].center.coordinates;
            const [longitude, latitude] = coordinates;
            this.initializeMap(latitude, longitude);
            this.loadHeatmapData();
          } else {
            this.isLoadingMap = false;
            this.errorMessage = 'Δεν βρέθηκαν συντεταγμένες τοποθεσίας στο προφίλ σας.';
          }
        },
        error: (error) => {
          this.isLoadingMap = false;
          this.errorMessage = 'Σφάλμα κατά τη φόρτωση δεδομένων χρήστη.';
        }
      });
  }

  private initializeMap(latitude: number, longitude: number): void {
    if (this.map) {
      this.map.setView([latitude, longitude], 12);
      return;
    }

    this.map = L.map('map', {
      center: [latitude, longitude],
      zoom: 13,
      zoomControl: true
    });

    this.map.whenReady(() => {
      setTimeout(() => {
        this.map.invalidateSize();
        this.map.setView([latitude, longitude], 12);
      }, 200);
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(this.map);
  }

  private loadHeatmapData(): void {
    const filters = this.buildFilters();
    this.appService.get_amea_locations(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (locations: HeatmapLocation[]) => {
          this.isLoadingMap = false;
          this.cdr.detectChanges();

          if (locations?.length > 0) {
            this.createHeatLayer(locations);
          }

          setTimeout(() => {
            if (this.map) {
              this.map.invalidateSize();
            }
          }, 300);
        }
      });
  }

  private buildFilters(): any {
    const problemType = this.filterForm.get('problemType')?.value;
    const disabilities = this.filterForm.get('disabilities')?.value || [];

    const filters: any = {
      activity_problem: (problemType !== '' && problemType !== null) ? [problemType] : [0, 1],
      disabilities: disabilities.length > 0 ? disabilities : []
    };

    return filters;
  }

  private createHeatLayer(locations: HeatmapLocation[]): void {
    if (this.heatLayer) {
      this.map.removeLayer(this.heatLayer);
    }

    const heatData: Array<[number, number, number]> = locations.map(point => [
      point.latitude,
      point.longtitude,
      1.0
    ]);

    this.heatLayer = L.heatLayer(heatData, {
      radius: 80,
      blur: 50,
      maxZoom: 18,
      minOpacity: 0.3,
      max: 1.0,
      gradient: {
        0.2: 'blue',
        0.4: 'lime',
        0.6: 'yellow',
        0.8: 'orange',
        1.0: 'red'
      }
    });

    this.heatLayer.addTo(this.map);
  }

 
  private loadStatistics(): void {
    this.isLoadingStats = true;

    forkJoin({
      activityProblems: this.appService.count_activity_problems(),
      ageGroups: this.appService.count_ages()
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.activityProblemsStats = data.activityProblems;
          this.ageGroupsStats = data.ageGroups;

          this.buildDisabilityLegend();
          this.calculateChartHeight();

          this.isLoadingStats = false;
          this.cdr.markForCheck();

         
          setTimeout(() => {
            this.animateBarSegmentsWithJS();
          }, 100);
        },
        error: (err) => {
          console.error('Error loading statistics:', err);
          this.isLoadingStats = false;
          this.cdr.markForCheck();
        }
      });
  }

  private animateBarSegmentsWithJS(): void {
    
    const barGroups = document.querySelectorAll('.stacked-bars-chart .stacked-bar-group');

    
    const segmentsByLayer: HTMLElement[][] = [];

    barGroups.forEach((barGroup) => {
      const segments = Array.from(barGroup.querySelectorAll('.bar-segment')) as HTMLElement[];

     

      segments.forEach((segment, layerIndex) => {
        if (!segmentsByLayer[layerIndex]) {
          segmentsByLayer[layerIndex] = [];
        }
        segmentsByLayer[layerIndex].push(segment);
      });
    });

    console.log('Segments by layer:', segmentsByLayer.map(layer => layer.length));

    
    segmentsByLayer.forEach((layerSegments, layerIndex) => {
      layerSegments.forEach(segment => {
        
        segment.style.transformOrigin = 'bottom';
        segment.style.transform = 'scaleY(0)';
        segment.style.opacity = '0';

        
        setTimeout(() => {
          segment.style.transition = 'transform 0.8s ease-out, opacity 0.5s ease-in';
          segment.style.transform = 'scaleY(1)';
          segment.style.opacity = '1';
        }, layerIndex * 500); 
      });
    });

    
    const activityBars = document.querySelectorAll('.activity-bars-chart .activity-bar');
    activityBars.forEach((bar, index) => {
      const barElement = bar as HTMLElement;

      barElement.style.transformOrigin = 'bottom';
      barElement.style.transform = 'scaleY(0)';
      barElement.style.opacity = '0';

      setTimeout(() => {
        barElement.style.transition = 'transform 0.6s ease-out, opacity 0.6s ease-out';
        barElement.style.transform = 'scaleY(1)';
        barElement.style.opacity = '1';
      }, index * 200);
    });
  }
  trackByDisability(index: number, item: any): string {
    
    return item.disability || item.name || index;
  }
  navigateToCertifiedClubs(): void {
    this.router.navigate(['/admin/certified_clubs']);
  }

  navigateToPendingClubs(): void {
    this.router.navigate(['/admin/certification/clubs']);
  }

  selectCarouselSlide(slideIndex: number): void {
    this.currentCarouselSlide = slideIndex;
    const carouselElement = document.querySelector('.carousel-container');
    if (carouselElement) {
      (carouselElement as HTMLElement).style.setProperty('--slide-index', slideIndex.toString());
    }
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  getPendingClubsList(): Array<{ clubName: string; count: number }> {
    return Object.values(this.usersPendingByClub);
  }

  getDisabilityItems(disabilities: { [key: string]: number }): Array<{ key: string, value: number }> {
    return Object.entries(disabilities).map(([key, value]) => ({ key, value }));
  }

  onFilterChange(): void {
    this.errorMessage = '';
    this.isLoadingMap = true;
    this.loadHeatmapData();
    this.loadStatistics();
  }

  resetFilters(): void {
    this.filterForm.reset({
      problemType: '',
      disabilities: []
    });
    this.onFilterChange();
  }

  calculateChartHeight(): number {
    return 300;
  }

  getDisabilitySegments(ageGroup: any): BarSegment[] {
    if (!ageGroup.disabilities) return [];

    const max = this.maxAgeValue; // Use the unified rounded max
    const totalChartHeight = 300;

    const segments: BarSegment[] = Object.entries(ageGroup.disabilities)
      .filter(([_, count]) => (count as number) > 0)
      .map(([name, count]) => {
        const countValue = count as number;
        return {
          disability: name,
          count: countValue,
          // Height is now a ratio of the Y-axis maximum
          height: (countValue / max) * totalChartHeight,
          percentage: (countValue / (ageGroup.count || 1)) * 100,
          color: this.getDisabilityColor(name)
        };
      });

    return segments.sort((a, b) => b.count - a.count);
  }

  getDisabilityColor(disabilityName: string): string {
    if (this.disabilityColors[disabilityName]) {
      return this.disabilityColors[disabilityName];
    }

    const upperName = disabilityName.toUpperCase();
    if (this.disabilityColors[upperName]) {
      return this.disabilityColors[upperName];
    }

    return '#999999';
  }

  getYAxisTicks(): number[] {
    const max = this.maxAgeValue; // Use the unified rounded max
    const ticks = [];
    const step = Math.max(1, Math.ceil(max / 5));

    for (let i = 0; i <= max; i += step) {
      ticks.push(i);
    }
    return ticks.reverse();
  }

  buildDisabilityLegend(): void {
    const disabilitySet = new Set<string>();

    this.ageGroupsStats.forEach(ageGroup => {
      if (ageGroup.disabilities) {
        Object.keys(ageGroup.disabilities).forEach(disability => {
          disabilitySet.add(disability);
        });
      }
    });

    this.disabilityLegendItems = Array.from(disabilitySet)
      .sort()
      .map(disability => ({
        name: disability,
        color: this.getDisabilityColor(disability)
      }));
  }

  private getActivityProblemsMax(): number {
    if (!this.activityProblemsStats) return 10;
    return Math.max(
      this.activityProblemsStats.no_activity_problems || 0,
      this.activityProblemsStats.activity_problems || 0
    );
  }

  sortDisabilitiesByCount(disabilities: Array<{ count: number, disability: string }>): Array<{ count: number, disability: string }> {
    return disabilities.slice().sort((a, b) => b.count - a.count);
  }

  getActivityYAxisTicks(): number[] {
    const max = this.maxActivityValue; // Use the unified rounded max
    const ticks = [];
    const step = Math.max(1, Math.ceil(max / 5));

    for (let i = 0; i <= max; i += step) {
      ticks.push(i);
    }
    return ticks.reverse();
  }
  /* Calculates the highest total stack height 
 * across all age groups and rounds up to nearest 5.
 */
  get maxAgeValue(): number {
    if (!this.ageGroupsStats || this.ageGroupsStats.length === 0) return 10;

    const totals = this.ageGroupsStats.map(group =>
      Object.values(group.disabilities).reduce((sum: number, count: any) => sum + (count || 0), 0)
    );

    const absoluteMax = Math.max(...totals);
    // Round up to nearest 5 (e.g., 11 becomes 15) to give the chart room at the top
    return Math.ceil(absoluteMax / 5) * 5 || 10;
  }

  /** * Problem 2 Fix: Calculates the highest single disability count 
   * and rounds up to nearest 5.
   */
  get maxActivityValue(): number {
    if (!this.activityProblemsStats?.disabilities || this.activityProblemsStats.disabilities.length === 0) return 10;

    const counts = this.activityProblemsStats.disabilities.map(d => d.count || 0);
    const absoluteMax = Math.max(...counts);

    return Math.ceil(absoluteMax / 5) * 5 || 10;
  }
  get maxTotalPeopleInAnyAgeGroup(): number {
    if (!this.ageGroupsStats) return 1;

    // Calculate the sum of all disabilities for each age group and find the max
    const totals = this.ageGroupsStats.map(group =>
      Object.values(group.disabilities).reduce((a: any, b: any) => a + b, 0)
    );

    return Math.max(...totals, 1); // Ensure we don't divide by zero
  }
  calculateActivityBarHeight(count: number): number {
    const max = this.maxActivityValue; // Use the unified rounded max
    const chartHeight = 300;

    // Now if count is 8 and max is 10, height will be exactly 80% of 300px
    return (count / max) * chartHeight;
  }

  onDisabilityHover(name: string): void {
    this.activeDisability = name;
  }

  onDisabilityLeave(): void {
    this.activeDisability = null;
  }

  onRightChartDisabilityHover(name: string): void {
    this.activeRightChartDisability = name;
  }

  onRightChartDisabilityLeave(): void {
    this.activeRightChartDisability = null;
  }

  // downloadChart(): void {
  //   const element = document.querySelector('.stacked-bars-chart');
  //   if (!element) {
  //     console.error('Chart element not found');
  //     return;
  //   }

  //   import('html2canvas').then((html2canvas) => {
  //     html2canvas.default(element as HTMLElement, {
  //       backgroundColor: '#ffffff',
  //       scale: 2,
  //       logging: false,
  //       useCORS: true,
  //       allowTaint: true,
  //       removeContainer: true
  //     }).then((canvas) => {
  //       const link = document.createElement('a');
  //       link.href = canvas.toDataURL('image/png');
  //       link.download = `age-group-chart-${Date.now()}.png`;
  //       link.click();
  //     }).catch(err => console.error('Download error:', err));
  //   }).catch(err => console.error('Import html2canvas error:', err));
  // }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.map) {
      this.map.remove();
    }
  }
}